import ErrorOutline from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import type { ReactNode, FC } from 'react';
import React, { useState, useEffect } from 'react';

import type { TraceProps, WithTrace } from '../../utils/trace';
import { withTrace } from '../../utils/trace';

export type SpinnerAllProps = SpinnerProps & WithTrace & TraceProps;

export interface SpinnerProps {
    hasError?: boolean;
    errorText?: ReactNode | string;
    align?: 'center' | 'flex-end';
    justify?: 'center' | 'flex-end';
    position?: 'absolute' | 'relative';
    color?: 'primary' | 'inherit';
    size?: number;
    timeout?: number;
    timeoutMessage?: ReactNode | string;
}

export const ErrorMessage = ({ errorText }: Partial<SpinnerProps>) => (
    <Grid item style={{ textAlign: 'center' }}>
        <ErrorOutline color="error" fontSize="large" />
        <Typography variant="h4" color="error">
            {errorText}
        </Typography>
    </Grid>
);

export const calcCenter = (size: number) => `calc(50% - ${size / 2}px)`;

export function SpinnerBase({
    hasError = false,
    errorText = '',
    justify = 'center',
    align = 'center',
    position = 'absolute',
    color = 'primary',
    size = 40,
    timeout = 5_000,
    timeoutMessage,
    applyTrace,
}: SpinnerAllProps) {
    const [timedOut, setTimedOut] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setTimedOut(true), timeout);
        return () => clearTimeout(timer);
    }, [timeout]);
    return (
        <Grid container justifyContent={justify} alignItems={align}>
            {hasError ? (
                <ErrorMessage errorText={errorText} />
            ) : (
                <Grid item style={{ position, top: calcCenter(size), textAlign: 'center' }}>
                    <CircularProgress size={size} color={color} {...applyTrace('loading')} />
                    <Typography style={{ visibility: timedOut ? 'visible' : 'hidden' }}>
                        {timeoutMessage}
                    </Typography>
                </Grid>
            )}
        </Grid>
    );
}

export const Spinner: FC<SpinnerProps> = withTrace('spinner')(SpinnerBase);
